import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, LogIn } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { JoinLeaderboardModal } from '../components/ui/modals/JoinLeaderboardModal';
import { usePrivateLeaderboards } from '../hooks/usePrivateLeaderboards';

export const JoinLeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code');
  const { user, signInWithGoogle } = useAuth();
  const [showJoinModal, setShowJoinModal] = useState(false);

  const {
    joinLeaderboard,
    loading,
    userGroups
  } = usePrivateLeaderboards();

  // Auto-open join modal when user is authenticated and we have an invite code
  useEffect(() => {
    if (user && inviteCode && !showJoinModal) {
      setShowJoinModal(true);
    }
  }, [user, inviteCode, showJoinModal]);

  // Redirect if no invite code is provided
  useEffect(() => {
    if (!inviteCode) {
      navigate('/popular');
    }
  }, [inviteCode, navigate]);

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
      // After sign-in, the useEffect above will auto-open the modal
    } catch (error) {
      console.error('Sign-in failed:', error);
    }
  };

  const handleCloseModal = () => {
    setShowJoinModal(false);
    navigate('/popular');
  };

  const handleJoinSuccess = (groupId?: string) => {
    setShowJoinModal(false);
    navigate('/popular');
  };

  // If not authenticated, show sign-in prompt
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-500/5 to-purple-500/10">
        <div className="max-w-md w-full bg-background rounded-xl shadow-xl p-8 text-center space-y-6">
          <div className="flex justify-center">
            <Users className="h-20 w-20 text-blue-500" />
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-foreground">Join Private Leaderboard</h1>
            <p className="text-muted-foreground">
              You've been invited to join a private leaderboard! Sign in to accept this invitation.
            </p>
          </div>

          {inviteCode && (
            <div className="bg-gray-100 dark:bg-gray-900/40 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-2">Invite Code</p>
              <p className="font-mono text-lg font-semibold">{inviteCode}</p>
            </div>
          )}

          <div className="space-y-3 pt-4">
            <Button
              onClick={handleSignIn}
              variant="primary"
              className="w-full flex items-center justify-center gap-2"
            >
              <LogIn className="w-4 h-4" />
              Sign In with Google
            </Button>
            <Button
              onClick={() => navigate('/popular')}
              variant="outline"
              className="w-full"
            >
              Cancel
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Create a free account to compete on private leaderboards with friends
          </p>
        </div>
      </div>
    );
  }

  // If authenticated, show join modal
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <JoinLeaderboardModal
        isOpen={showJoinModal}
        onClose={handleCloseModal}
        joinLeaderboard={joinLeaderboard}
        isProcessing={loading}
        userGroupCount={userGroups.length}
        onSuccess={handleJoinSuccess}
        prefilledCode={inviteCode || undefined}
      />

      {/* Fallback content if modal isn't showing yet */}
      {!showJoinModal && (
        <div className="text-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      )}
    </div>
  );
};
